(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var SECTION_ID = 'predictive-search';

  function debounce(fn, delay) {
    var timer;
    return function debounced() {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

   
  function trapFocus(container, event) {
    var focusable = Array.prototype.filter.call(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (el) {
        return el.offsetParent !== null && !el.closest('[hidden]');
      }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!container.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  class PredictiveSearchDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-search-dialog]');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaultState = this.querySelector('[data-search-default]');
      this.status = this.querySelector('[data-search-status]');
      this.suggestUrl = this.dataset.suggestUrl || '/search/suggest';
      this.resourceTypes = this.dataset.resourceTypes || 'product';
      this.minLength = parseInt(this.dataset.minLength, 10) || 1;
      this.isDropdown = this.dataset.variant === 'dropdown';
      this.clearButton = this.querySelector('[data-search-clear]');
      this.activeIndex = -1;
 
      if (!this.dialog && !this.isDropdown) return;

      if (this.dialog) {
        this.dialog.addEventListener('hidden.bs.offcanvas', () => {
          this.clearResults();
          if (this.opener && document.contains(this.opener)) this.opener.focus();
        });

        this.dialog.addEventListener('shown.bs.offcanvas', () => {
          if (this.input) this.input.focus();
        });

        this._onDocClick = (event) => {
          var opener = event.target.closest('[data-search-drawer-open]');
          if (!opener) return;
          event.preventDefault();
          this.opener = opener;
          this.open();
        };
        document.addEventListener('click', this._onDocClick);

        this._onTrapKeydown = (event) => {
          if (event.key !== 'Tab' || !this.isOpen) return;
          trapFocus(this.dialog, event);
        };
        document.addEventListener('keydown', this._onTrapKeydown);
      }

      if (this.isDropdown) { 
        this._onOutsideClick = (event) => {
          if (!this.isOpen) return;
          if (this.contains(event.target)) return;
          this.close();
        };
        document.addEventListener('click', this._onOutsideClick);
 
        this._pointerDownInside = false;
        this.addEventListener('pointerdown', () => {
          this._pointerDownInside = true;
          setTimeout(() => { this._pointerDownInside = false; }, 0);
        });

        this.addEventListener('focusout', (event) => {
          if (!this.isOpen) return;
          if (this._pointerDownInside) return;
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.close();
        });
      }

      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-search-clear]')) return;
        event.preventDefault();
        if (this._controller) this._controller.abort();
        if (this.input) this.input.value = '';
        this.clearResults();
        if (this.input) this.input.focus();
      });

      this._debouncedFetch = debounce(this.fetchResults.bind(this), DEBOUNCE_MS);

      if (this.input) {
        this.input.addEventListener('input', () => {
          var query = this.input.value.trim();
          this.toggleClearButton();
          if (query.length < this.minLength) {
            if (this._controller) this._controller.abort();
            this.clearResults();
            return;
          }
          this.showResults();
          this._debouncedFetch(query);
        });

        this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      }

      this.addEventListener('click', (event) => {
        var pill = event.target.closest('[data-search-term]');
        if (!pill) return;
        event.preventDefault();
        var term = pill.dataset.searchTerm || '';
        if (!this.input) return;
        this.input.value = term;
        this.toggleClearButton();
        this.input.focus();
        if (term.trim().length < this.minLength) return;
        this.showResults();
        this.fetchResults(term.trim());
      });
    }

    disconnectedCallback() {
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
      if (this._onTrapKeydown) document.removeEventListener('keydown', this._onTrapKeydown);
      if (this._onOutsideClick) document.removeEventListener('click', this._onOutsideClick);
      if (this._controller) this._controller.abort();

      if (window.bootstrap && bootstrap.Offcanvas && this.dialog) {
        var instance = bootstrap.Offcanvas.getInstance(this.dialog);
        if (instance) instance.dispose();
      } 
      document.querySelectorAll('.offcanvas-backdrop').forEach(function (b) {
        b.remove();
      });
      document.body.classList.remove('offcanvas-backdrop', 'modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }

    getOffcanvas() {
      if (window.bootstrap && bootstrap.Offcanvas && this.dialog) {
        return bootstrap.Offcanvas.getOrCreateInstance(this.dialog);
      }
      return null;
    }

    get isOpen() {
      if (this.isDropdown) return !!(this.results && !this.results.hidden);
      return this.dialog.classList.contains('show');
    }

    open() {
      if (this.isOpen) return;
      if (this.isDropdown) {
        this.showResults();
        return;
      }
      var offcanvas = this.getOffcanvas();
      if (offcanvas) offcanvas.show();
    }

    close() {
      if (!this.isOpen) return;
      if (this.isDropdown) { 
        if (this._controller) this._controller.abort();
        this.results.hidden = true;
        if (this.input) {
          this.input.setAttribute('aria-expanded', 'false');
          this.input.removeAttribute('aria-activedescendant');
        }
        this.clearHighlight();
        this.activeIndex = -1;
        this.toggleDropdownBackdrop(false);
        return;
      }
      var offcanvas = this.getOffcanvas();
      if (offcanvas) offcanvas.hide();
    }

    buildUrl(query) {
      var params = new URLSearchParams({
        q: query,
        section_id: SECTION_ID,
        'resources[type]': this.resourceTypes,
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'hide',
      });
      return this.suggestUrl + '?' + params.toString();
    }

    fetchResults(query) {
      if (this._controller) this._controller.abort();
      this._controller = new AbortController();

      if (this.results) this.results.setAttribute('aria-busy', 'true');

      var self = this;
      fetch(this.buildUrl(query), { signal: this._controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search failed');
          return response.text();
        })
        .then(function (html) {
          self.renderResults(html, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          console.error('[predictive-search]', error);
          if (self.results) self.results.setAttribute('aria-busy', 'false');
        });
    }

    renderResults(html, query) {
      if (!this.results) return; 
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var inner = doc.querySelector('.predictive-search__results-inner');

      this.results.innerHTML = inner ? inner.outerHTML : '';
      this.results.setAttribute('aria-busy', 'false');
      this.activeIndex = -1;

      if (!this.results.hidden && this.results.childElementCount) this.toggleDropdownBackdrop(true);

      this.bindTabs();

      var options = this.getOptions();
      var count = inner ? parseInt(inner.dataset.resultsCount, 10) || 0 : 0;

      if (this.input) {
        this.input.setAttribute('aria-expanded', options.length ? 'true' : 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
      if (this.status) {
        var resultsText = this.status.dataset.resultsText;
        var noResultsText = this.status.dataset.noResultsText;
        this.status.textContent = count
          ? (resultsText ? resultsText.replace('[count]', count).replace('[terms]', query) : count + ' results for ' + query)
          : (noResultsText ? noResultsText.replace('[terms]', query) : 'No results for ' + query);
      }
    }

    bindTabs() {
      var tabs = this.results.querySelectorAll('[data-search-tab]');
      if (!tabs.length) return;
      var self = this;
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          self.selectTab(tab.dataset.searchTab);
        });
      });
    }

    selectTab(name) {
      this.clearHighlight();
      this.activeIndex = -1;

      this.results.querySelectorAll('[data-search-tab]').forEach(function (tab) {
        tab.setAttribute('aria-selected', tab.dataset.searchTab === name ? 'true' : 'false');
      });
      this.results.querySelectorAll('[data-search-panel]').forEach(function (panel) {
        panel.hidden = panel.dataset.searchPanel !== name;
      });
    }
 
    showResults() {
      if (this.defaultState) this.defaultState.hidden = true;
      if (this.results) this.results.hidden = false; 
      if (this.results && this.results.childElementCount) this.toggleDropdownBackdrop(true);
    }

    showDefault() {
      if (this.defaultState) this.defaultState.hidden = false;
      if (this.results) this.results.hidden = true;
      this.toggleDropdownBackdrop(false);
    }

    toggleDropdownBackdrop(open) {
      if (!this.isDropdown) return;
      var willOpen = !!open;
      var isOpen = document.body.classList.contains('header-search-dropdown-open');
      if (willOpen && !isOpen) {
        var gap = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = gap > 0 ? gap + 'px' : '';
      } else if (!willOpen && isOpen) {
        document.body.style.paddingRight = '';
      }
      document.body.classList.toggle('header-search-dropdown-open', willOpen);
    }

    clearResults() {
      if (this.results) {
        this.results.innerHTML = '';
        this.results.setAttribute('aria-busy', 'false');
      }
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
      if (this.status) this.status.textContent = '';
      this.activeIndex = -1;
      this.toggleClearButton();
      this.showDefault();
    }

    toggleClearButton() {
      if (!this.clearButton) return;
      this.clearButton.hidden = !(this.input && this.input.value.length);
    }

    getOptions() {
      if (!this.results) return []; 
      var nodes = this.results.querySelectorAll(
        '[data-search-panel]:not([hidden]) .product-card,' +
          '[data-search-panel]:not([hidden]) [data-search-result],' +
          '.predictive-search__results-inner > [data-search-result]'
      );
      return Array.prototype.slice.call(nodes);
    }

    onInputKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      var options = this.getOptions();
      if (!options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
        this.highlight(options);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.highlight(options);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        var target = options[this.activeIndex];
        var link = target.matches('a') ? target : target.querySelector('a[href]');
        if (link) link.click();
      }
    }

    clearHighlight() {
      if (!this.results) return;
      this.results.querySelectorAll('.is-active').forEach(function (el) {
        el.classList.remove('is-active');
      });
      if (this.input) this.input.removeAttribute('aria-activedescendant');
    }

    highlight(options) {
      var self = this;
      options.forEach(function (option, index) {
        var active = index === self.activeIndex;
        option.classList.toggle('is-active', active);
        if (active) {
          if (!option.id) option.id = 'ps-option-' + index;
          if (self.input) self.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }
  }

  if (!customElements.get('predictive-search-drawer')) {
    customElements.define('predictive-search-drawer', PredictiveSearchDrawer);
  }
})();
